import { createListenerMiddleware } from '@reduxjs/toolkit'
import { MedSummaryEntryEditActions } from '@/medical-billing-summaries/entries/store/entries-edit.slice'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'

/**
 * Observes the Medical Billing Summary entry-edit flow purely from the outside: it never
 * dispatches anything the UI reads, so it cannot alter the save/validation flow it watches
 * (see analytics/README.md, "Analytics is observational"). Uses a dedicated listener rather than
 * editing entries-edit.listeners.ts's business logic directly.
 *
 * Both actions below carry their full context (parentId/entryId/fieldKey) in the payload, captured
 * by entries-edit.listeners.ts at dispatch time. This listener never reads `activeEdit` from state:
 * doing so would tie correctness to dispatch ordering elsewhere (e.g. whether `activeEdit` has
 * already been cleared by the time this effect runs).
 */
export const medSummaryEntryEditAnalyticsMiddleware = createListenerMiddleware()
const start = medSummaryEntryEditAnalyticsMiddleware.startListening

start({
  actionCreator: MedSummaryEntryEditActions.saveFieldSucceeded,
  effect: (action) => {
    const track = getTrackEventGlobal()
    if (!track) return

    track(AnalyticsEvent.MedSummaryAssetEntryFieldSaved, {
      medSummaryAssetId: action.payload.parentId,
      medSummaryEntryId: action.payload.entryId,
      medSummaryEntryField: action.payload.fieldKey,
    })
  },
})

start({
  actionCreator: MedSummaryEntryEditActions.saveFieldFailed,
  effect: (action) => {
    const track = getTrackEventGlobal()
    if (!track) return

    const { parentId, entryId, fieldKey, ...errorProps } = action.payload
    track(AnalyticsEvent.MedSummaryAssetEntryFieldSaveFailed, {
      medSummaryAssetId: parentId,
      medSummaryEntryId: entryId,
      medSummaryEntryField: fieldKey,
      ...errorProps,
    })
  },
})
